import React, { useState, useMemo, useContext } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import CallModal from "../../Components/CallModal";
import TestDriveModal from "../../Components/TestDriveModal";
import ProtectionOrderModal from "../../Components/ProtectionOrderModal";
import RelatedBrand from "../../Components/RelatedBrand";
import RelatedModel from "../../Components/RelatedModel";
import CarActionButtons from "../../Components/CarActionButtons";
import PageLoadGate from "../../Components/Skeleton/PageLoadGate";
import { CarDetailsSkeleton } from "../../Components/Skeleton/Skeleton";
import "./CarDetails.css";
import { MyContext } from "../../Context/MyContext";
import { resolveImage, resolveImages } from "../../../Utils/imageUtils";
import { getAccessToken } from "../../../services/api";
import { calculateEmi } from "../../../Utils/emiCalculator";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaTachometerAlt,
  FaGasPump,
  FaCogs,
  FaChair,
  FaShieldAlt,
  FaCheckCircle,
  FaCalendarAlt,
  FaPalette,
  FaCarSide,
  FaChevronLeft,
  FaChevronRight,
  FaPlay,
  FaFilePdf,
  FaExternalLinkAlt,
} from "react-icons/fa";

const formatTemplate = (template = "", vars = {}) =>
  Object.entries(vars).reduce(
    (text, [key, value]) => text.replace(new RegExp(`\\{${key}\\}`, "g"), value ?? ""),
    template
  );

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const getScoreTier = (score, tiers = {}) => {
  const ordered = ["excellent", "good", "fair", "poor"];
  for (const key of ordered) {
    const tier = tiers[key];
    if (tier && score >= (tier.min ?? 0)) return tier;
  }
  return tiers.poor || { label: "Score" };
};

const categoryPercent = (category) => {
  if (!category.total) return 0;
  return Math.round(((category.score || 0) / category.total) * 100);
};

export default function CarDetails() {
  const {
    cars,
    brands,
    model,
    fuel_type,
    transmission,
    seats,
    featuers,
    car_color,
    body_type,
    getContactsForCar,
    car_trust,
    warranty_page,
    emi_page,
    currentUser,
  } = useContext(MyContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const car = useMemo(() => {
    const preview = location.state?.previewCar;
    const matchesId = (item) =>
      item &&
      (String(item.id) === String(id) ||
        String(item._id) === String(id) ||
        String(item.legacyId) === String(id));

    const fromList = cars.find(matchesId);
    if (fromList) return fromList;
    if (matchesId(preview)) return preview;
    return null;
  }, [cars, id, location.state?.previewCar]);

  const [current, setCurrent] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showTestDriveModal, setShowTestDriveModal] = useState(false);
  const [showProtectionModal, setShowProtectionModal] = useState(false);
  const [inspectionPhotoIndex, setInspectionPhotoIndex] = useState(0);

  const slides = useMemo(() => {
    if (!car) return [];
    const imageSlides = resolveImages(car.images || []);
    if (car.youtube?.trim()) {
      return [...imageSlides, { type: "youtube", url: car.youtube }];
    }
    return imageSlides;
  }, [car]);

  const renderContent = () => {
    if (!car) {
      return (
        <div className="cd-empty">
          <span>Car</span>
          <h3>Car Not Found</h3>
          <p>The vehicle you are looking for does not exist.</p>
          <button type="button" onClick={() => navigate("/cars")}>
            Browse All Cars
          </button>
        </div>
      );
    }

    const trust = car_trust || {};
    const overview = trust.overview || {};
    const sidebar = trust.sidebar || {};
    const inspectionConfig = trust.inspection || {};
    const warrantyConfig = trust.warranty || {};
    const testDriveConfig = trust.testDrive || {};
    const inspection = car.inspection || {};
    const warranty = car.warranty || {};
    const tabs = trust.tabs || [];

    const getBrandName = (brandId) => brands.find((b) => b.id === brandId)?.name || "-";
    const getModelName = (modelId) => model.find((m) => m.id === modelId)?.name || "-";
    const getFuelName = (fuelId) => fuel_type.find((f) => f.id === fuelId)?.name || "-";
    const getTransName = (transId) => transmission.find((t) => t.id === transId)?.name || "-";
    const getSeatsName = (seatId) => seats.find((s) => s.id === seatId)?.name || "-";
    const getColorName = (colorId) => car_color.find((c) => c.id === colorId)?.name || "-";
    const getBodyName = (bodyId) => body_type.find((b) => b.id === bodyId)?.name || "-";

    const featureNames = (car.feature_ids || [])
      .map((fid) => featuers.find((f) => f.id === fid)?.name || "")
      .filter(Boolean);
    const inspectionCategories = inspection.categories?.length
      ? inspection.categories
      : (inspectionConfig.defaultCategories || []).map((item) => ({ ...item, status: "pending" }));

    const handleCall = () => {
      setSelectedPartners(getContactsForCar(car.sub_register_id));
      setShowCallModal(true);
    };

    const handleWhatsapp = () => {
      const matched = getContactsForCar(car.sub_register_id);
      if (matched.length > 0) {
        window.open(`https://wa.me/91${matched[0].ph_num}`, "_blank");
      }
    };

    const handleTestDrive = () => {
      if (!getAccessToken()) {
        navigate(testDriveConfig.loginPath || "/login");
        return;
      }
      setShowTestDriveModal(true);
    };

    const title = `${car.year} ${getBrandName(car.brand_id)} ${getModelName(car.model_id)}`;
    const emiTenure = emi_page?.calculator?.defaultTenureMonths || 60;
    const emiRate = emi_page?.calculator?.defaultInterestRate || 10.5;
    const downPct = emi_page?.calculator?.defaultDownPaymentPercent || 20;
    const loanAmount = car.price * (1 - downPct / 100);
    const emiEstimate = Math.round(calculateEmi(loanAmount, emiRate, emiTenure).emi);
    const emiPath = `${sidebar.emiCalculatorPath || "/finance"}?${sidebar.emiQueryParam || "price"}=${car.price}`;
    const protectionPlans = warranty_page?.plans || [];
    const orderConfig = warranty_page?.order || {};

    const overallScore =
      inspection.overallScore ||
      (inspection.totalPoints > 0
        ? Math.round(((inspection.passedPoints || 0) / inspection.totalPoints) * 100)
        : 0);
    const scoreTier = getScoreTier(overallScore, inspectionConfig.scoreTiers);
    const inspectionPhotos = (inspection.photos || []).filter(Boolean);

    const handleProtectionReserve = () => {
      if (!getAccessToken()) {
        navigate(warrantyConfig.reserveLoginPath || orderConfig.loginPath || "/login");
        return;
      }
      setShowProtectionModal(true);
    };

    const specRows = [
      { label: "Brand", value: getBrandName(car.brand_id) },
      { label: "Model", value: getModelName(car.model_id) },
      { label: "Body Type", value: getBodyName(car.body_type_id) },
      { label: "Year", value: car.year },
      { label: "Fuel Type", value: getFuelName(car.fuel_type_id) },
      { label: "Transmission", value: getTransName(car.transmission_id) },
      { label: "Seats", value: getSeatsName(car.seats_id) },
      { label: "Color", value: getColorName(car.color_id) },
      { label: "KM Driven", value: `${car.km_driven.toLocaleString()} km` },
      { label: "Tire Wear", value: `${car.tire_wear}%` },
      { label: "Location", value: car.location || sidebar.missingLocation },
      { label: "Listing Status", value: car.status || "Available" },
    ];

    const nextSlide = () => setCurrent((prev) => (prev + 1) % slides.length);
    const prevSlide = () =>
      setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));

    return (
      <div className="cd-page">
        <div className="cd-topbar">
          <div className="cd-topbar-inner">
            <button type="button" className="cd-back" onClick={() => navigate(-1)}>
              <FaChevronLeft /> Back
            </button>
            <nav className="cd-breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/cars">Cars</Link>
              <span>/</span>
              <span>{getBrandName(car.brand_id)}</span>
            </nav>
          </div>
        </div>

        <div className="cd-shell">
          <div className="cd-main">
            <div className="cd-gallery-card">
              <div className="cd-gallery-main">
                {slides.length > 1 && (
                  <>
                    <button type="button" className="cd-gallery-nav prev" onClick={prevSlide}>
                      <FaChevronLeft />
                    </button>
                    <button type="button" className="cd-gallery-nav next" onClick={nextSlide}>
                      <FaChevronRight />
                    </button>
                    <span className="cd-slide-counter">
                      {current + 1} / {slides.length}
                    </span>
                  </>
                )}

                <div className="cd-slide-viewport">
                  {typeof slides[current] === "string" ? (
                    <img src={resolveImage(slides[current])} alt={title} />
                  ) : slides[current]?.type === "youtube" ? (
                    <iframe src={slides[current].url} title="Video tour" allowFullScreen />
                  ) : null}
                </div>
              </div>

              {slides.length > 1 && (
                <div className="cd-thumbs">
                  {slides.map((slide, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`cd-thumb ${current === index ? "active" : ""}`}
                      onClick={() => setCurrent(index)}
                    >
                      {typeof slide === "string" ? (
                        <img src={resolveImage(slide)} alt={`View ${index + 1}`} />
                      ) : (
                        <span className="cd-thumb-video">
                          <FaPlay />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="cd-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`cd-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cd-tab-panel">
              {activeTab === "overview" && (
                <div className="cd-overview">
                  <div className="cd-highlights">
                    {(overview.highlights || []).map((item) => (
                      <div className="cd-highlight" key={item.key}>
                        {item.key === "quality" ? <FaShieldAlt /> : item.key === "verified" ? <FaCheckCircle /> : <FaCarSide />}
                        <div>
                          <strong>
                            {item.key === "condition"
                              ? car.is_modify === 1
                                ? item.modifiedTitle
                                : item.standardTitle
                              : item.title}
                          </strong>
                          <span>
                            {item.key === "condition"
                              ? car.is_modify === 1
                                ? item.modifiedDescription
                                : item.standardDescription
                              : item.description}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="cd-description">
                    <h3>{overview.descriptionTitle}</h3>
                    <p>
                      {formatTemplate(overview.descriptionTemplate, {
                        title,
                        bodyType: getBodyName(car.body_type_id).toLowerCase(),
                        fuelType: getFuelName(car.fuel_type_id).toLowerCase(),
                        transmission: getTransName(car.transmission_id).toLowerCase(),
                      })}
                    </p>
                  </div>

                  {car.youtube && (
                    <div className="cd-media-block">
                      <h3>{overview.videoTitle}</h3>
                      <div className="cd-video-wrap">
                        <iframe src={car.youtube} title="Car video" allowFullScreen />
                      </div>
                    </div>
                  )}

                  {car.shorts && (
                    <div className="cd-media-block">
                      <h3>{overview.shortsTitle}</h3>
                      <div className="cd-shorts-wrap">
                        <iframe src={car.shorts} title="Car short" allowFullScreen />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "specifications" && (
                <div className="cd-spec-table-wrap">
                  <table className="cd-spec-table">
                    <tbody>
                      {specRows.map((row) => (
                        <tr key={row.label}>
                          <th>{row.label}</th>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "features" && (
                <div className="cd-features-grid">
                  {featureNames.length > 0 ? (
                    featureNames.map((name) => (
                      <div key={name} className="cd-feature-item">
                        <FaCheckCircle />
                        <span>{name}</span>
                      </div>
                    ))
                  ) : (
                    <p className="cd-no-features">{overview.emptyFeatures}</p>
                  )}
                </div>
              )}

              {activeTab === "inspection" && (
                <div className="cd-inspection">
                  <div className="cd-section-head">
                    <div>
                      <h3>{inspectionConfig.title}</h3>
                      <p>
                        {inspection.totalPoints > 0
                          ? formatTemplate(inspectionConfig.subtitleTemplate, {
                              passed: inspection.passedPoints || 0,
                              total: inspection.totalPoints,
                            })
                          : inspectionConfig.pendingMessage}
                      </p>
                    </div>
                    {inspection.lastInspectedAt && (
                      <span>
                        {inspectionConfig.lastInspectedLabel}: {formatDate(inspection.lastInspectedAt)}
                      </span>
                    )}
                  </div>

                  {overallScore > 0 && (
                    <div className="cd-score-panel">
                      <div
                        className="cd-score-ring"
                        style={{ "--score": overallScore }}
                        aria-label={`${overallScore} out of 100`}
                      >
                        <strong>{overallScore}</strong>
                        <small>{inspectionConfig.overallScoreSuffix || "/ 100"}</small>
                      </div>
                      <div>
                        <span className="cd-score-label">{inspectionConfig.overallScoreLabel}</span>
                        <strong className="cd-score-tier">{scoreTier.label}</strong>
                        {inspection.reportPdf && (
                          <a
                            href={inspection.reportPdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cd-pdf-link"
                          >
                            <FaFilePdf /> {inspectionConfig.pdfLabel}
                            <FaExternalLinkAlt />
                          </a>
                        )}
                        {!inspection.reportPdf && (
                          <p className="cd-pdf-muted">{inspectionConfig.pdfUnavailable}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {inspection.summary && <p className="cd-inspection-summary">{inspection.summary}</p>}

                  {inspectionCategories.some((c) => c.total > 0) && (
                    <div className="cd-score-breakdown">
                      <h4>{inspectionConfig.scoreBreakdownTitle}</h4>
                      {inspectionCategories
                        .filter((c) => c.total > 0)
                        .map((category) => (
                          <div key={`breakdown-${category.key || category.title}`} className="cd-score-bar-row">
                            <span>{category.title}</span>
                            <div className="cd-score-bar-track">
                              <span style={{ width: `${categoryPercent(category)}%` }} />
                            </div>
                            <strong>{categoryPercent(category)}%</strong>
                          </div>
                        ))}
                    </div>
                  )}

                  {inspectionPhotos.length > 0 && (
                    <div className="cd-inspection-gallery">
                      <h4>{inspectionConfig.photoGalleryTitle}</h4>
                      <p className="cd-gallery-sub">{inspectionConfig.photoGallerySubtitle}</p>
                      <div className="cd-inspection-gallery-main">
                        <img
                          src={resolveImage(inspectionPhotos[inspectionPhotoIndex])}
                          alt={`Inspection ${inspectionPhotoIndex + 1}`}
                        />
                      </div>
                      <div className="cd-inspection-gallery-thumbs">
                        {inspectionPhotos.map((photo, index) => (
                          <button
                            key={`insp-photo-${index}`}
                            type="button"
                            className={inspectionPhotoIndex === index ? "active" : ""}
                            onClick={() => setInspectionPhotoIndex(index)}
                          >
                            <img src={resolveImage(photo)} alt={`Inspection thumb ${index + 1}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="cd-inspection-grid">
                    {inspectionCategories.map((category) => (
                      <div key={category.key || category.title} className={`cd-inspection-card status-${category.status || "pending"}`}>
                        <div className="cd-inspection-card-head">
                          <h4>{category.title}</h4>
                          <span>{inspectionConfig.statusLabels?.[category.status] || inspectionConfig.statusLabels?.pending}</span>
                        </div>
                        {category.total > 0 && (
                          <strong>
                            {formatTemplate(inspectionConfig.categoryScoreLabel, {
                              score: category.score || 0,
                              total: category.total,
                            })}
                          </strong>
                        )}
                        {category.items?.length > 0 && (
                          <ul>
                            {category.items.map((item, index) => (
                              <li key={`${item.label}-${index}`}>
                                <FaCheckCircle />
                                <span>{item.label}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                  {inspection.history?.length > 0 && (
                    <div className="cd-history-list">
                      {inspection.history.map((item) => (
                        <div key={item.label}>
                          <span>{item.label}</span>
                          <strong>{item.value}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "warranty" && (
                <div className="cd-warranty">
                  <div className="cd-section-head">
                    <div>
                      <h3>{warrantyConfig.title}</h3>
                      <p>{warranty.package || warrantyConfig.pendingMessage}</p>
                    </div>
                    <Link to={warrantyConfig.policyCtaPath || "/warranty"} className="cd-policy-link">
                      {warrantyConfig.policyCtaLabel}
                    </Link>
                  </div>
                  <div className="cd-warranty-grid">
                    {warranty.package && (
                      <div>
                        <span>{warrantyConfig.packageLabel}</span>
                        <strong>{warranty.package}</strong>
                      </div>
                    )}
                    {warranty.supportDays > 0 && (
                      <div>
                        <span>{formatTemplate(warrantyConfig.supportDaysLabel, { days: warranty.supportDays })}</span>
                        <strong>{trust.badges?.verified}</strong>
                      </div>
                    )}
                    <div>
                      <span>{warrantyConfig.engineGearboxLabel}</span>
                      <strong>{warranty.engineGearbox ? trust.badges?.verified : warrantyConfig.pendingTitle}</strong>
                    </div>
                    <div>
                      <span>
                        {warranty.returnEligible
                          ? formatTemplate(warrantyConfig.returnEligibleLabel, { days: warranty.returnDays || 7 })
                          : warrantyConfig.returnNotEligibleLabel}
                      </span>
                      <strong>{warranty.returnEligible ? trust.badges?.certified : trust.badges?.standard}</strong>
                    </div>
                  </div>

                  {protectionPlans.length > 0 && (
                    <div className="cd-warranty-plans">
                      <h4>{warrantyConfig.plansTitle}</h4>
                      <p>{warrantyConfig.plansSubtitle}</p>
                      <div className="cd-warranty-plan-cards">
                        {protectionPlans.map((plan) => (
                          <div key={plan.id} className="cd-warranty-plan-card">
                            {plan.badge && <span className="cd-plan-badge">{plan.badge}</span>}
                            <strong>{plan.name}</strong>
                            <ul>
                              {(plan.highlights || []).slice(0, 2).map((h) => (
                                <li key={h}>{h}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {(warranty.returnEligible || inspection.isCertified) && (
                        <button type="button" className="cd-btn-protection" onClick={handleProtectionReserve}>
                          <FaShieldAlt /> {warrantyConfig.reserveCtaLabel}
                        </button>
                      )}
                    </div>
                  )}

                  {(orderConfig.lifecycle || []).length > 0 && (
                    <div className="cd-lifecycle">
                      <h4>{warrantyConfig.lifecycleTitle}</h4>
                      <p>{warrantyConfig.lifecycleSubtitle}</p>
                      <div className="cd-lifecycle-steps">
                        {orderConfig.lifecycle.map((step, index) => (
                          <div key={step.status} className="cd-lifecycle-step">
                            <span>{index + 1}</span>
                            <div>
                              <strong>{step.label}</strong>
                              <p>{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="cd-warranty-lists">
                    {warranty.inclusions?.length > 0 && (
                      <div>
                        <h4>{warrantyConfig.inclusionsTitle}</h4>
                        <ul>{warranty.inclusions.map((item) => <li key={item}>{item}</li>)}</ul>
                      </div>
                    )}
                    {warranty.exclusions?.length > 0 && (
                      <div>
                        <h4>{warrantyConfig.exclusionsTitle}</h4>
                        <ul>{warranty.exclusions.map((item) => <li key={item}>{item}</li>)}</ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <aside className="cd-sidebar">
            <div className="cd-sidebar-card">
              <div className="cd-badge-row">
                {car.is_modify === 1 && <span className="cd-badge">{trust.badges?.modified}</span>}
                {inspection.isCertified && <span className="cd-badge">{trust.badges?.certified}</span>}
                {car.status && <span className="cd-badge cd-badge--muted">{car.status}</span>}
              </div>

              <h1 className="cd-title">{title}</h1>

              <div className="cd-price-row">
                <div>
                  <span className="cd-price-label">{sidebar.priceLabel}</span>
                  <div className="cd-price">Rs.{car.price.toLocaleString()}</div>
                </div>
                <div className="cd-views">{car.view_count || 0} {sidebar.viewsSuffix}</div>
              </div>

              <div className="cd-emi">
                <span>{sidebar.emiLabel}</span>
                <strong>Rs.{emiEstimate.toLocaleString()}/mo</strong>
                <small>{sidebar.emiTenureLabel}</small>
                <Link to={emiPath} className="cd-emi-link">
                  {sidebar.emiCalculatorLabel || "Calculate EMI"}
                </Link>
              </div>

              <div className="cd-quick-specs">
                <div className="cd-quick-spec">
                  <FaTachometerAlt />
                  <span>{car.km_driven.toLocaleString()} km</span>
                </div>
                <div className="cd-quick-spec">
                  <FaGasPump />
                  <span>{getFuelName(car.fuel_type_id)}</span>
                </div>
                <div className="cd-quick-spec">
                  <FaCogs />
                  <span>{getTransName(car.transmission_id)}</span>
                </div>
                <div className="cd-quick-spec">
                  <FaChair />
                  <span>{getSeatsName(car.seats_id)}</span>
                </div>
                <div className="cd-quick-spec">
                  <FaCalendarAlt />
                  <span>{car.year}</span>
                </div>
                <div className="cd-quick-spec">
                  <FaPalette />
                  <span>{getColorName(car.color_id)}</span>
                </div>
              </div>

              <div className="cd-location-box">
                <FaMapMarkerAlt />
                <div>
                  <span>{sidebar.locationLabel}</span>
                  <p>{car.location || sidebar.missingLocation}</p>
                </div>
              </div>

              <div className="cd-sidebar-actions">
                <CarActionButtons car={car} variant="detail" />
                <button type="button" className="cd-btn-call" onClick={handleCall}>
                  <FaPhoneAlt /> {sidebar.requestCallLabel}
                </button>
                {car.testDriveEnabled !== false && (
                  <button type="button" className="cd-btn-test" onClick={handleTestDrive}>
                    <FaCarSide /> {sidebar.bookTestDriveLabel}
                  </button>
                )}
                <button type="button" className="cd-btn-wa" onClick={handleWhatsapp}>
                  <FaWhatsapp /> {sidebar.whatsappLabel}
                </button>
              </div>

              <ul className="cd-trust-list">
                {(sidebar.trustPoints || []).map((point) => (
                  <li key={point}><FaCheckCircle /> {point}</li>
                ))}
                <li>
                  <FaCheckCircle />
                  <Link to={sidebar.warrantyPolicyPath || "/warranty"}>
                    {sidebar.warrantyPolicyLabel}
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>

        <div className="cd-mobile-bar">
          <div className="cd-mobile-price">Rs.{car.price.toLocaleString()}</div>
          <div className="cd-mobile-actions">
            <button type="button" className="cd-btn-call" onClick={handleCall}>
              <FaPhoneAlt /> {sidebar.mobileCallLabel}
            </button>
            {car.testDriveEnabled !== false && (
              <button type="button" className="cd-btn-test" onClick={handleTestDrive} aria-label={sidebar.bookTestDriveLabel}>
                <FaCarSide />
              </button>
            )}
            <button type="button" className="cd-btn-wa" onClick={handleWhatsapp} aria-label={sidebar.whatsappLabel}>
              <FaWhatsapp />
            </button>
          </div>
        </div>

        {showCallModal && (
          <CallModal
            partners={selectedPartners}
            close={() => setShowCallModal(false)}
          />
        )}
        {showTestDriveModal && (
          <TestDriveModal
            car={car}
            config={testDriveConfig}
            currentUser={currentUser}
            onClose={() => setShowTestDriveModal(false)}
          />
        )}
        {showProtectionModal && (
          <ProtectionOrderModal
            car={car}
            carTitle={title}
            plans={protectionPlans}
            orderConfig={orderConfig}
            onClose={() => setShowProtectionModal(false)}
          />
        )}

        <RelatedBrand brandId={car.brand_id} currentId={car.id} />
        <RelatedModel modelId={car.model_id} currentId={car.id} />
      </div>
    );
  };

  return (
    <PageLoadGate skeleton={<CarDetailsSkeleton />} delay={380}>
      {renderContent()}
    </PageLoadGate>
  );
}
